import { Button, Card, Form, Input, Progress, Typography, notification } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useState } from "react";
import { changePassword } from "../services/accountsService";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

function scorePassword(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)             score++;
  if (/[A-Z]/.test(pw))          score++;
  if (/[a-z]/.test(pw))          score++;
  if (/\d/.test(pw))             score++;
  if (/[@$!%*?&]/.test(pw))      score++;
  return score;
}

const STRENGTH_LABEL  = ["", "Very Weak", "Weak", "Fair", "Strong", "Very Strong"];
const STRENGTH_COLOR  = ["", "#ff4d4f", "#ff7a45", "#ffa940", "#52c41a", "#52c41a"];

export default function ChangePasswordForm() {
  const [form]    = Form.useForm();
  const [loading, setLoading]   = useState(false);
  const [newPw,   setNewPw]     = useState("");
  const { logout }              = useAuth();
  const navigate                = useNavigate();

  const score = scorePassword(newPw);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await changePassword({
        current_password:  values.current_password,
        new_password:      values.new_password,
        confirm_password:  values.confirm_password,
      });
      notification.success({
        message: "Password Changed",
        description: "Your password has been updated. You will be logged out in 3 seconds.",
        duration: 3,
      });
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 3000);
    } catch (error) {
      const data = error.response?.data;

      if (data) {
        const fieldErrors = [];

        if (data.current_password) {
          fieldErrors.push({
            name: "current_password",
            errors: Array.isArray(data.current_password) ? data.current_password : [data.current_password],
          });
        }
        if (data.new_password) {
          fieldErrors.push({
            name: "new_password",
            errors: Array.isArray(data.new_password) ? data.new_password : [data.new_password],
          });
        }
        if (data.confirm_password) {
          fieldErrors.push({
            name: "confirm_password",
            errors: Array.isArray(data.confirm_password) ? data.confirm_password : [data.confirm_password],
          });
        }

        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
        } else {
          notification.error({
            message: "Password Change Failed",
            description: data.detail || data.non_field_errors?.[0] || "Please check your input and try again.",
          });
        }
      } else {
        notification.error({
          message: "Connection Error",
          description: "Unable to connect. Please try again.",
        });
      }
      setNewPw("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: 480, marginTop: 24 }}>
      <Title level={5} style={{ marginBottom: 20 }}>
        <LockOutlined style={{ marginRight: 8 }} />
        Change Password
      </Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          label="Current Password"
          name="current_password"
          rules={[{ required: true, message: "Please enter your current password." }]}
        >
          <Input.Password placeholder="Current password" />
        </Form.Item>

        <Form.Item
          label="New Password"
          name="new_password"
          rules={[{ required: true, message: "Please enter a new password." }]}
        >
          <Input.Password
            placeholder="New password"
            onChange={(e) => setNewPw(e.target.value)}
          />
        </Form.Item>

        {newPw && (
          <div style={{ marginBottom: 16 }}>
            <Progress
              percent={(score / 5) * 100}
              showInfo={false}
              strokeColor={STRENGTH_COLOR[score]}
              size="small"
            />
            <Text style={{ color: STRENGTH_COLOR[score], fontSize: 12 }}>
              {STRENGTH_LABEL[score]}
            </Text>
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
              8+ chars · uppercase · lowercase · number · special (@$!%*?&)
            </Text>
          </div>
        )}

        <Form.Item
          label="Confirm New Password"
          name="confirm_password"
          rules={[{ required: true, message: "Please confirm your new password." }]}
        >
          <Input.Password placeholder="Confirm new password" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Change Password
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}
